import React from 'react';
import {
  AggregationSection,
  ListSection,
  RangeSection,
  RangeGroupSection,
  BooleanSection,
  ProductCollectionFilter,
} from '../../../../types';
import List from './sections/list';
import Range from './sections/range';
import RangeGroup from './sections/range-group';
import Boolean from './sections/boolean';

export type SupportedSection =
  | ListSection
  | RangeSection
  | RangeGroupSection
  | BooleanSection;
type Props = {
  section: SupportedSection;
  useInfiniteScroll?: boolean;
  appliedFilters?: ProductCollectionFilter[];
  showTitle?: boolean;
};

export const isValidSection = (
  section: AggregationSection
): section is SupportedSection => {
  return [
    'ListSection',
    'RangeSection',
    'RangeGroupSection',
    'BooleanSection',
  ].includes(section.__typename || '');
};

export const isDisabledSection = (section: AggregationSection) => {
  switch (section.__typename) {
    case 'ListSection':
      return !section.items || !section.items.length;
    case 'RangeSection':
      return section.min === section.max;
    default:
      return false;
  }
};

const getSelectedListItems = (filter: ProductCollectionFilter | undefined) => {
  if (!filter) return undefined;

  switch (filter.__typename) {
    case 'ProductCollectionTermFilter':
      return filter.value
        ? [{ id: filter.id, value: filter.value }]
        : undefined;

    case 'ProductCollectionTermsFilter':
      return filter.values
        ? filter.values
            .map((value, index) => ({ id: filter.ids[index], value }))
            .sort((a, b) => a.value.localeCompare(b.value))
        : undefined;

    default:
      return undefined;
  }
};

const FilterSection = ({
  section,
  useInfiniteScroll,
  appliedFilters,
  showTitle,
}: Props) => {
  if (!isValidSection(section)) {
    return null;
  }

  switch (section.__typename) {
    case 'ListSection':
      return (
        <List
          {...section}
          useInfiniteScroll={useInfiniteScroll}
          selectedItems={
            appliedFilters &&
            getSelectedListItems(
              appliedFilters.find(
                (filter: ProductCollectionFilter) =>
                  filter.property === section.property
              )
            )
          }
          showTitle={showTitle}
        />
      );
    case 'RangeSection':
      return <Range {...section} showTitle={showTitle} />;
    case 'RangeGroupSection':
      return <RangeGroup {...section} showTitle={showTitle} />;
    case 'BooleanSection':
      return <Boolean {...section} showTitle={showTitle} />;
    default:
      return null;
  }
};

export default FilterSection;
